import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import loadingFileIcon from '../../../../assets/icons/loadingFile.svg';
import FileInput from '../../../../components/FileInput.component';
import { ModalWithButtons } from '../../../../components/ModalWithButtons/ModalWithButtons.component';
import { LeavePrompt } from '../../../../components/Prompt/LeavePrompt/LeavePrompt.component';
import { examinersImportJson } from '../../../../const/exampleFile';
import { useLayoutContext } from '../../../../Context/Layout.context';
import { ImportUsersStatus } from '../../../../models/user.types';
import { uploadUsersFile } from '../../../../services/users/users.service';
import { useStyles as importTeamsUseStyles } from '../../../ExaminerTreemap/Actions/ImportTeams.styles';

const exampleFile = examinersImportJson;

export interface ImportExaminersFromFileProps {
  permissions: { hasAdminExaminerCreate: boolean; hasAdminOkeExaminerCreate: boolean };
  onClose: () => void;
}

export const ImportExaminersFromFile: React.FC<ImportExaminersFromFileProps> = (props) => {
  const { t } = useTranslation();
  const importTeamsClasses = importTeamsUseStyles();
  const { openAlert, showLoader } = useLayoutContext();
  const [examinersFile, setExaminersFile] = useState<File>();
  const [isFileLoading, setIsFileLoading] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<
    'Niepoprawny format pliku.' | 'Wystąpił błąd podczas ładowania.'
  >();
  const [leavePromptOpen, setLeavePromptOpen] = useState<boolean>(false);

  const handleFileLoaded = (files: FileList) => {
    if (files.length === 1 && files[0].type === 'application/json') {
      setIsFileLoading(true);
      setExaminersFile(files[0]);
    }
  };

  useEffect(() => {
    if (examinersFile !== undefined) {
      examinersFile
        .text()
        .then((stringifiedJson: string) => {
          try {
            JSON.parse(stringifiedJson);
            setValidationError(undefined);
          } catch (error) {
            setValidationError(
              error instanceof SyntaxError ? 'Niepoprawny format pliku.' : 'Wystąpił błąd podczas ładowania.',
            );
          }
        })
        .catch((error) => setValidationError('Wystąpił błąd podczas ładowania.'))
        .finally(() => setIsFileLoading(false));
    }
  }, [examinersFile]);

  const handleOnConfirm = () => {
    if (examinersFile) {
      showLoader(true);
      uploadUsersFile(
        examinersFile,
        props.permissions.hasAdminExaminerCreate && props.permissions.hasAdminOkeExaminerCreate
          ? false
          : props.permissions.hasAdminOkeExaminerCreate,
      )
        .then((result) => {
          switch (result.data) {
            case ImportUsersStatus.Success:
              openAlert('success');
              setExaminersFile(undefined);
              props.onClose();
              break;
            case ImportUsersStatus.WrongFileExtension:
              setValidationError('Niepoprawny format pliku.');
              break;
            case ImportUsersStatus.EmptyOrWrongFileContent:
              setValidationError('Wystąpił błąd podczas ładowania.');
              break;
            case ImportUsersStatus.Failed:
              openAlert('error');
              break;
          }
        })
        .catch(() => openAlert('error'))
        .finally(() => showLoader(false));
    }
  };

  const closeModal = () => {
    if (examinersFile !== undefined) {
      setLeavePromptOpen(true);
    } else props.onClose();
  };

  return (
    <>
      <ModalWithButtons
        open
        title="Import z pliku"
        xCloseButton={closeModal}
        onCancel={closeModal}
        confirmButtonDisabled={examinersFile === undefined || validationError !== undefined}
        onConfirm={handleOnConfirm}
        autoHeight
      >
        <Grid
          container
          justifyContent="center"
          alignContent="center"
          style={{ minHeight: '500px', textAlign: 'center' }}
          spacing={2}
        >
          <Grid item xs={12}>
            <Typography variant="h3" className={importTeamsClasses.h6}>
              Import Egzaminatorów z pliku
            </Typography>
          </Grid>
          <Grid item xs={7}>
            <FileInput
              onFilesLoaded={handleFileLoaded}
              hint={'Zaimportowane pliki powinny mieć format json,'}
              exampleFile={exampleFile}
              acceptedFileExtensions={['json']}
            />
          </Grid>
          {examinersFile !== undefined && (
            <Grid item xs={7}>
              <Grid container alignItems="center">
                <Grid item xs={1}>
                  {isFileLoading ? (
                    <img src={loadingFileIcon} alt="ikona pobierania pliku" />
                  ) : validationError !== undefined ? (
                    <AddCircleIcon htmlColor="#BC0000" style={{ transform: 'rotate(45deg)' }} />
                  ) : (
                    <CheckCircleIcon htmlColor="#347F42" />
                  )}
                </Grid>
                <Grid item xs={10} style={{ textAlign: 'left' }}>
                  <Typography>{examinersFile.name}</Typography>
                </Grid>
                <Grid item xs={1}>
                  <Button
                    onClick={() => setExaminersFile(undefined)}
                    style={{
                      minWidth: 'unset',
                      color: 'rgba(44, 45, 62, 0.8)',
                    }}
                  >
                    <CloseIcon />
                  </Button>
                </Grid>
              </Grid>
              {validationError !== undefined && (
                <div className={importTeamsClasses.validationError}>{validationError}</div>
              )}
            </Grid>
          )}
        </Grid>
      </ModalWithButtons>
      <LeavePrompt
        isOpen={leavePromptOpen}
        onConfirm={() => {
          setExaminersFile(undefined);
          setLeavePromptOpen(false);
          props.onClose();
        }}
        onCancel={() => setLeavePromptOpen(false)}
      />
    </>
  );
};
